import { useEffect, useId, useState } from "react"
import style from "./Dropdown.module.css"
import GetSpace from "../../utils/space"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { useTranslation } from "react-i18next"
import type { InteractionDescriptionProps } from "../InteractionTooltip/InteractionTooltip"

type SelectOption =
  | string
  | {
      label: string
      value: string
    }

interface GenericDropDownProps extends InteractionDescriptionProps {
  name?: string
  legend?: string
  initialState?: string | number | null
  data: SelectOption[]
  disable?: boolean
  handlerFuncion: (event: React.ChangeEvent<HTMLSelectElement>) => void
  delay?: string
  animationTypes?: keyof typeof AnimationTypes
  animationDuration?: string
  id?: string
  errorMessage?: string
}

export const GenericDropDown: React.FC<GenericDropDownProps> = ({
  name = "",
  legend = "",
  initialState = null,
  disable,
  data,
  handlerFuncion,
  delay = "0s",
  animationDuration,
  animationTypes = AnimationTypes.fadeInTraslate,
  id,
  errorMessage,
  descriptionKey,
}) => {
  const { t } = useTranslation("interactionDescriptions")

  const generatedSelectId = useId()

  const selectId = id || generatedSelectId

  const errorId = `${selectId}-error`

  const hasError = Boolean(errorMessage)

  const options = data.map((item) =>
    typeof item === "string" ? { label: item, value: item } : { label: item.label, value: item.value },
  )

  const computeInitial = () => {
    if (!options.length) return ""

    if (initialState === null || initialState === undefined) {
      return options[0].value
    }

    if (typeof initialState === "number") {
      return options[initialState]?.value ?? options[0].value
    }

    const s = String(initialState).trim()

    if (!s) {
      return options[0].value
    }

    const byValue = options.find((o) => o.value === s)

    if (byValue) {
      return byValue.value
    }

    const byLabel = options.find((o) => o.label.toLowerCase() === s.toLowerCase())

    if (byLabel) {
      return byLabel.value
    }

    return options[0].value
  }

  const [value, setValue] = useState<string>(computeInitial)

  useEffect(() => {
    const next = computeInitial()

    if (next !== value) {
      setValue(next)
    }
  }, [initialState, data])

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const next = event.target.value

    setValue(next)

    handlerFuncion(event)
  }

  return (
    <Animate type={animationTypes} duration={animationDuration} delay={delay}>
      <div className={style.divDropDown} data-interaction-description-key={descriptionKey}>
        {legend && (
          <>
            <h5 className={hasError ? style.errorLegend : undefined}>{legend}</h5>

            <GetSpace bottom={5} />
          </>
        )}

        <select
          data-interaction-description-key={descriptionKey}
          id={selectId}
          disabled={disable}
          name={name}
          className={`${style.dropDonw} ${hasError ? style.dropDownError : ""}`}
          onChange={handleChange}
          value={value}
          aria-invalid={hasError}
          aria-description={t(descriptionKey)}
          aria-describedby={hasError ? errorId : undefined}
        >
          {options.map((opt, index) => (
            <option key={`${opt.value}-${index}`} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {hasError && (
          <p id={errorId} className={style.errorMessage} role="alert">
            {errorMessage}
          </p>
        )}
      </div>
    </Animate>
  )
}
